import React, { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { CurrentUser } from "../../stores/CurrentUserStore/Types";
import { useAppDispatch } from "../../stores/CommunityStore";
import { putRequest } from "../../utils/httpRequest";
import { fetchPosts } from "../../stores/CommunityStore/posts/actions/fetchPosts";
import { Post } from "../../stores/CommunityStore/posts/types";
import { fetchTools } from "../../stores/CommunityStore/tools/actions/fetchTools";
import { Tool } from "../../stores/CommunityStore/tools/types";
import { Topic } from "../../stores/CommunityStore/topics/types";
import HouseRulesModal from "./components/Shell/HouseRulesModal";
import NotificationPreferencesModal from "./components/Shell/NotificationPreferencesModal";
import Posts from "./components/Shell/Posts";
import Sidebar from "./components/Shell/Sidebar";
import Tools from "./components/Shell/Tools";

interface ShellProps {
  isSidebarOpen: boolean;
  onCloseSidebar: () => void;
  currentUser: CurrentUser;
  posts: Post[];
  postsLoading: boolean;
  topics: Topic[];
  featuredPosts: Post[];
  tools: Tool[];
  toolsLoading: boolean;
  getCurrentUser: () => Promise<void>;
  targetReplyId?: string;
  loadInitialTopicPosts: boolean;
}

const Shell = ({
  isSidebarOpen,
  onCloseSidebar,
  currentUser,
  posts,
  postsLoading,
  topics,
  featuredPosts,
  tools,
  toolsLoading,
  getCurrentUser,
  targetReplyId,
  loadInitialTopicPosts,
}: ShellProps) => {
  const dispatch = useAppDispatch();
  const [isHouseRulesOpen, setIsHouseRulesOpen] = useState(false);
  const [isAcceptingHouseRules, setIsAcceptingHouseRules] = useState(false);
  const [houseRulesError, setHouseRulesError] = useState("");
  const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] =
    useState(false);
  const [activeView, setActiveView] = useState<"posts" | "tools">("posts");
  const [selectedTopicName, setSelectedTopicName] = useState("");
  const [selectedTopicUuid, setSelectedTopicUuid] = useState<string>();
  const [activeTargetReplyId, setActiveTargetReplyId] = useState(targetReplyId);
  const firstTopic = topics[0];
  const requiresHouseRulesAcceptance =
    currentUser.kind === "current-user" &&
    !currentUser.community_rules_accepted;

  useEffect(() => {
    if (selectedTopicUuid || !firstTopic || !loadInitialTopicPosts) {
      return;
    }

    setSelectedTopicName(firstTopic.name);
    setSelectedTopicUuid(firstTopic.uuid);
    dispatch(fetchPosts({ topicUuid: firstTopic.uuid }));
  }, [dispatch, firstTopic, loadInitialTopicPosts, selectedTopicUuid]);

  const acceptHouseRules = async () => {
    setIsAcceptingHouseRules(true);
    setHouseRulesError("");

    try {
      await putRequest(`/api/users/${currentUser.id}.json`, {
        user: { community_rules_accepted: true },
      });
      await getCurrentUser();
      setIsHouseRulesOpen(false);
    } catch (requestError) {
      const axiosError = requestError as AxiosError;
      setHouseRulesError(axiosError.message || "Unable to accept house rules.");
    } finally {
      setIsAcceptingHouseRules(false);
    }
  };

  const showPosts = (topicName?: string, topicUuid?: string) => {
    const topic = topicName
      ? { name: topicName, uuid: topicUuid }
      : firstTopic;

    if (!topic) {
      return;
    }

    setActiveTargetReplyId(undefined);
    setSelectedTopicName(topic.name);
    setSelectedTopicUuid(topic.uuid);
    setActiveView("posts");
    dispatch(fetchPosts({ topicUuid: topic.uuid }));
  };

  const showFeaturedPost = (postUuid: string) => {
    setActiveTargetReplyId(undefined);
    setActiveView("posts");
    dispatch(fetchPosts({ featuredPostId: postUuid }));
  };

  const sidebarProps = {
    onOpenHouseRules: () => setIsHouseRulesOpen(true),
    onOpenNotificationSettings: () => setIsNotificationSettingsOpen(true),
    onSelectTopic: showPosts,
    onShowTools: () => {
      setActiveView("tools");
      dispatch(fetchTools());
    },
    selectedTopicName,
    isToolsOpen: activeView === "tools",
    topics,
    featuredPosts,
    onSelectFeaturedPost: showFeaturedPost,
  };

  return (
    <>
      <button
        className={`community-shell__sidebar-backdrop ${
          isSidebarOpen ? "community-shell__sidebar-backdrop--open" : ""
        }`}
        type="button"
        aria-label="Close community navigation"
        onClick={onCloseSidebar}
      />
      <div
        className={`community-shell__sidebar-panel ${
          isSidebarOpen ? "community-shell__sidebar-panel--open" : ""
        }`}
        id="community-sidebar-panel"
        aria-hidden={!isSidebarOpen}
      >
        <Sidebar onSelect={onCloseSidebar} {...sidebarProps} />
      </div>
      <section className="community-shell" aria-label="Community posts">
        <div className="community-shell__container">
          <div className="community-shell__grid">
            <Sidebar {...sidebarProps} />
            {activeView === "posts" ? (
              <Posts
                selectedTopicName={selectedTopicName}
                selectedTopicUuid={selectedTopicUuid}
                currentUser={currentUser}
                posts={posts}
                loading={postsLoading}
                targetReplyId={activeTargetReplyId}
              />
            ) : (
              <Tools
                onShowPosts={() => showPosts()}
                topicName={firstTopic?.name}
                tools={tools}
                loading={toolsLoading}
              />
            )}
          </div>
        </div>
      </section>
      <HouseRulesModal
        isOpen={requiresHouseRulesAcceptance || isHouseRulesOpen}
        requiresAcceptance={requiresHouseRulesAcceptance}
        isAccepting={isAcceptingHouseRules}
        error={houseRulesError}
        onAccept={acceptHouseRules}
        onClose={() => setIsHouseRulesOpen(false)}
      />
      <NotificationPreferencesModal
        isOpen={isNotificationSettingsOpen}
        onClose={() => setIsNotificationSettingsOpen(false)}
        currentUser={currentUser}
        getCurrentUser={getCurrentUser}
      />
    </>
  );
};

export default Shell;
